import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.addEventListener(ThemeEvents.subscriptionUpdate, this.#onSubscriptionUpdate);
    this.#syncOctoberOffer();
    // Sync on load: if a picker is already in the DOM with subscription selected, apply immediately
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.#syncFromPicker())
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    closestSection.removeEventListener(ThemeEvents.subscriptionUpdate, this.#onSubscriptionUpdate);
  }

  /**
   * On page load, reads the picker state directly and applies the subscription UI
   * without waiting for an event — covers the race between picker and price connectedCallbacks.
   */
  #syncFromPicker() {
    const section = this.closest('.shopify-section, dialog');
    if (!section) return;
    const picker = section.querySelector(
      `subscription-picker-component[data-product-id="${this.dataset.productId}"]`
    );
    if (!picker) return;
    const isSubscription = picker.querySelector('[value="subscription"]')?.checked;
    const selectedPlan = picker.querySelector('[ref="planOptions"] .is-selected[data-plan-id]')
      ?? picker.querySelector('[ref="planOptions"] [data-plan-id]');
    const price = Number(isSubscription ? selectedPlan?.dataset.price : picker.dataset.variantPrice);
    if (!Number.isFinite(price)) return;
    const moneyFormat = picker.dataset.moneyFormat || '{{amount}}';
    const currency = picker.dataset.currency || 'USD';

    this.#onSubscriptionUpdate({
      detail: {
        productId: this.dataset.productId,
        purchaseType: isSubscription ? 'subscription' : 'onetime',
        price,
        formattedPrice: formatMoney(price, moneyFormat, currency),
      },
    });
  }

  /** @type {string | null} */
  #originalPrice = null;

  #syncOctoberOffer() {
    if (!this.hasAttribute('data-october-offer')) return;
    const container = this.querySelector('[ref="priceContainer"]');
    const price = container?.querySelector('.price');
    if (!container || !price) return;

    let row = container.querySelector('.shavingart__subscription-price-row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'shavingart__subscription-price-row';
      container.appendChild(row);
      row.appendChild(price);
    }

    let badges = row.querySelector('.shavingart__price-badges');
    if (!badges) {
      badges = document.createElement('div');
      badges.className = 'shavingart__price-badges';
      row.appendChild(badges);
    }
    const subscriptionBadge = row.querySelector('.shavingart__subscribe-save-pill');
    if (subscriptionBadge) badges.appendChild(subscriptionBadge);

    let offer = badges.querySelector('.shavingart__october-offer-pill');
    if (!offer) {
      offer = document.createElement('span');
      offer.className = 'shavingart__october-offer-pill';
      offer.textContent = '$15 off $75+ | $25 off $125+';
    }
    badges.appendChild(offer);
  }

  /**
   * @param {CustomEvent} event
   */
  #onSubscriptionUpdate = (event) => {
    if (String(event.detail?.productId) !== this.dataset.productId) return;

    const priceElement = this.querySelector('[ref="priceContainer"] .price') ?? this.querySelector('.price');
    if (!priceElement || !event.detail.formattedPrice) return;

    const priceContainer = this.querySelector('[ref="priceContainer"]') ?? priceElement.closest('div');

    if (event.detail.purchaseType === 'subscription') {
      // Keep the current variant's regular price only for the comparison label.
      const section = this.closest('.shopify-section, dialog');
      const picker = section?.querySelector(
        `subscription-picker-component[data-product-id="${this.dataset.productId}"]`
      );
      const regularPrice = picker?.querySelector('.shavingart__sub-card--onetime .shavingart__sub-card__price');
      this.#originalPrice = regularPrice?.textContent?.trim()
        || this.#originalPrice || priceElement.textContent?.trim() || null;

      // Show crossed-out original + discounted price
      const compareSpan = priceContainer?.querySelector('.shavingart__subscription-compare-price');
      if (!compareSpan && priceContainer) {
        const cs = document.createElement('span');
        cs.className = 'shavingart__subscription-compare-price';
        priceContainer.insertBefore(cs, priceContainer.firstChild);
      }
      const cp = priceContainer?.querySelector('.shavingart__subscription-compare-price');
      if (cp) {
        cp.textContent = this.#originalPrice ?? '';
        cp.hidden = false;
      }

      // Shopify's selected allocation already includes its selling-plan adjustment.
      priceElement.textContent = event.detail.formattedPrice;

      // Ensure a flex price-row wrapper exists to hold price + pill side-by-side
      let priceRow = priceContainer?.querySelector('.shavingart__subscription-price-row');
      if (!priceRow && priceContainer) {
        priceRow = document.createElement('div');
        priceRow.className = 'shavingart__subscription-price-row';
        // Move the price element into the row
        priceContainer.insertBefore(priceRow, priceElement);
        priceRow.appendChild(priceElement);
      }

      // Pill lives inside the price row
      let pill = priceRow?.querySelector('.shavingart__subscribe-save-pill')
        ?? this.querySelector('.shavingart__subscribe-save-pill');
      if (!pill) {
        pill = document.createElement('span');
        pill.className = 'shavingart__subscribe-save-pill';
        pill.setAttribute('aria-label', 'Subscribe and save 10%');
        (priceRow ?? priceContainer ?? priceElement).appendChild(pill);
      }
      pill.textContent = 'Subscribe & Save 10%';
      pill.hidden = false;
    } else {
      // One-time: restore original price, unwrap row, remove compare + pill
      priceElement.textContent = event.detail.formattedPrice;
      // Move price element back out of the row wrapper, then remove wrapper
      const priceRow = priceContainer?.querySelector('.shavingart__subscription-price-row');
      if (priceRow && priceContainer) {
        priceContainer.insertBefore(priceElement, priceRow);
        priceRow.remove();
      }
      priceContainer?.querySelector('.shavingart__subscription-compare-price')?.remove();
      this.querySelectorAll('.shavingart__subscribe-save-pill').forEach((p) => { p.hidden = true; });
    }
    this.#syncOctoberOffer();
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    // The picker and price can receive variant updates in either order. Read the
    // final picker state after both listeners have replaced their server markup.
    requestAnimationFrame(() => this.#syncFromPicker());

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Reset subscription state on variant change
    this.#originalPrice = null;
    const pill = this.querySelector('.shavingart__subscribe-save-pill');
    if (pill) pill.hidden = true;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
    this.#syncOctoberOffer();
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
